import React, { FC, useContext, ReactNode, useState } from 'react';
import find from 'lodash/find';
import get from 'lodash/get';
import nth from 'lodash/nth';
import styled, { css } from 'styled-components';

import ADAudioPlayer from '@ad-scripts/presentation/components/common/audio_player';
import FieldContainer from './FieldContainer';
import FormContext from 'src/presentation/contexts/FormContext';
import { decodeS3URI } from 'src/presentation/components/pages/shared/utils/Utils';

type AudioPlayerProps = {
  'data-aid'?: string;
  attr: string;
  description?: string | ReactNode;
  filled?: boolean;
  enablePlayOnError?: boolean;
  hideAudioPlayerIfNoUrl?: boolean;
  label?: string;
  showBorder?: boolean;
  trackLabel?: string;
  useFullUrlAsTrackLabel?: boolean;
  value?: string | number;
};

export const FILENAME_FROM_URL_REGEX = /\/?([^/]+$)/;

const AudioPlayer: FC<AudioPlayerProps> = ({
  'data-aid': dataAid,
  attr,
  description,
  filled,
  enablePlayOnError,
  hideAudioPlayerIfNoUrl,
  label,
  showBorder,
  trackLabel,
  useFullUrlAsTrackLabel,
  value,
}) => {
  const [componentError, setComponentError] = useState(null);
  const { errors, workingState } = useContext(FormContext);
  const error = find(errors, error => error.attr === attr) || componentError;
  const audioUrl = value ? value : get(workingState, attr, '');
  const decodedAudioUrl = decodeS3URI(audioUrl as string);

  const audioLabel =
    trackLabel ||
    (useFullUrlAsTrackLabel
      ? decodedAudioUrl
      : nth(decodedAudioUrl.match(FILENAME_FROM_URL_REGEX), 1) || decodedAudioUrl);

  const audioControl = (
    <ControlContainer filled={filled} showBorder={showBorder}>
      <ADAudioPlayer
        data-aid={dataAid}
        enablePlayOnError={enablePlayOnError}
        onError={setComponentError}
        trackLabel={audioLabel}
        url={audioUrl}
      />
    </ControlContainer>
  );

  const hideAudioControl = hideAudioPlayerIfNoUrl && !audioUrl;

  return (
    <AudioPlayerContainer
      data-aid={dataAid}
      description={description}
      error={error ? error.detail : null}
      label={label}
      control={hideAudioControl ? null : audioControl}
    />
  );
};

const ControlContainer = styled.div`
  ${p =>
    (p.showBorder || p.filled) &&
    css`
      display: flex;
      min-height: 32px;
      padding: 0 8px;
      width: 100%;
    `}

  ${p =>
    p.showBorder &&
    css`
      background-color: ${p => p.theme.colors.white};
      border-radius: 4px;
      border: 1px solid ${p => p.theme.colors.gray300};
    `}
  
  ${p =>
    p.filled &&
    css`
      background: ${p => p.theme.colors.gray100};
      border-radius: 19px;
      padding-left: 12px;
      padding-right: 12px;
    `}
`;

export const AudioPlayerContainer = styled(FieldContainer)`
  margin: 0;
  width: 100%;
`;

export default AudioPlayer;
